import ScrollReveal from '@/components/base/ScrollReveal';
import SectionHeading from '@/components/base/SectionHeading';
import { marketSessions } from '@/mocks/forexTrading';

const toneBar: Record<string, string> = {
  primary: 'bg-primary-500',
  background: 'bg-background-500',
  accent: 'bg-accent-500',
};

const toneDot: Record<string, string> = {
  primary: 'bg-primary-500',
  background: 'bg-background-600',
  accent: 'bg-accent-500',
};

const axisLabels = ['00:00', '06:00', '12:00', '18:00', '24:00'];

export default function MarketSessions() {
  return (
    <section className="border-y border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20 lg:px-10">
        <SectionHeading
          eyebrow="เวลาเทรด"
          title="ช่วงเวลาไหนตลาดลื่นไหล และช่วงไหนควรหลบ"
          subtitle="สภาพคล่องไม่เท่ากันตลอดวัน ช่วงที่ตลาดใหญ่คาบเกี่ยวกัน Spread จะแคบที่สุด ส่วนช่วงตลาดปิดราคาอาจกระโดดข้าม Stop Loss ได้"
        />

        <ScrollReveal className="mt-9">
          <div className="rounded-xl border border-background-200 bg-background-100/50 p-5 md:p-7">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-foreground-600">
                <span className="h-px w-5 bg-accent-500" />
                เวลาไทย (GMT+7)
              </p>
              <div className="flex flex-wrap items-center gap-3">
                {marketSessions.map((session) => (
                  <span key={session.name} className="flex items-center gap-1.5 text-xs text-foreground-600">
                    <span className={`h-2 w-2 rounded-full ${toneDot[session.tone]}`} />
                    {session.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-between font-num text-[11px] text-foreground-400">
              {axisLabels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>

            <div className="mt-2 flex flex-col gap-5">
              {marketSessions.map((session) => {
                const startPct = (session.start / 24) * 100;
                const endPct = (session.end / 24) * 100;
                const firstWidth = Math.max(0, Math.min(100 - startPct, endPct - startPct));
                const wrapWidth = endPct > 100 ? endPct - 100 : 0;

                return (
                  <div key={session.name}>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <p className="flex items-baseline gap-2">
                        <span className="font-heading text-sm font-semibold text-foreground-950">{session.name}</span>
                        <span className="font-num text-xs text-foreground-600">{session.hours}</span>
                      </p>
                      <span className="font-num text-xs text-foreground-500">{session.pairs}</span>
                    </div>

                    <div className="relative mt-2 h-7 w-full overflow-hidden rounded-md bg-background-200/70">
                      <div className="pointer-events-none absolute inset-0 grid grid-cols-4">
                        <span className="border-r border-background-300/60" />
                        <span className="border-r border-background-300/60" />
                        <span className="border-r border-background-300/60" />
                        <span />
                      </div>
                      <div
                        className={`absolute inset-y-0 rounded-md ${toneBar[session.tone]}`}
                        style={{ left: `${startPct}%`, width: `${firstWidth}%` }}
                      />
                      {wrapWidth > 0 && (
                        <div
                          className={`absolute inset-y-0 rounded-md ${toneBar[session.tone]}`}
                          style={{ left: '0%', width: `${wrapWidth}%` }}
                        />
                      )}
                    </div>

                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{session.note}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex flex-col gap-2 rounded-lg border border-background-300 border-l-2 border-l-accent-500 bg-background-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-700">
                <i className="ri-time-line mt-0.5 text-base text-accent-600" />
                ช่วง 19:30–23:00 ที่ลอนดอนกับนิวยอร์กเปิดพร้อมกัน คือช่วงที่ Spread แคบและคำสั่งลื่นไหลที่สุด
              </p>
              <span className="shrink-0 rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-semibold text-accent-800">
                ช่วงคาบเกี่ยว
              </span>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}